// @flow
import React from 'react';
import { groupBy } from 'lodash';

import { RECIPIENT_TYPES } from 'reducers/roleBasedMessagingSearch';
import { ChatIneligibleCopy, DisabledComposerListItemCopy } from 'constants/CopyConstants';
import ErrorReporter from 'app/components/ErrorReporter.jsx';
import OrganizationRecipientItem from './OrganizationRecipientItem.jsx';
import GroupRecipientItem from './GroupRecipientItem.jsx';
import GroupChatRecipientItem from './GroupChatRecipientItem.jsx';
import IndividualRecipientItem from './IndividualRecipientItem.jsx';
import type {
  Recipient,
  OrganizationRecipient,
  GroupRecipient,
  GroupChatRecipient,
  IndividualRecipient,
} from 'reducers/roleBasedMessagingSearch';
import SettingsStore from 'stores/SettingsStore';
import Text from 'components/Text.jsx';

type Props = {
  recipient: Recipient[],
  recipientIsSelected: boolean,
  districtIsSelected: boolean,
  nonDistrictRecipientIsSelected?: boolean,
  selectionLength: number,
};

export default class RecipientItem extends React.Component<Props, *> {
  render() {
    const {
      recipient,
      recipientIsSelected,
      nonDistrictRecipientIsSelected,
      selectionLength,
      districtIsSelected,
    } = this.props;
    const recipientByType = groupBy(recipient, r => r.type);
    const organizationRecipients: OrganizationRecipient[] =
      recipientByType[RECIPIENT_TYPES.ORGANIZATION_RECIPIENT];
    const groupRecipients: GroupRecipient[] = recipientByType[RECIPIENT_TYPES.GROUP_RECIPIENT];
    const groupChatRecipients: GroupChatRecipient[] =
      recipientByType[RECIPIENT_TYPES.GROUP_CHAT_RECIPIENT];
    const individualRecipients: IndividualRecipient[] =
      recipientByType[RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT];

    const sendToFewLimit =
      SettingsStore.getSetting('composer', 'send_to_few', 'recipient_limit') || 20;
    const aboveSendToFewLimit = selectionLength >= sendToFewLimit;
    const anotherDistrictIsSelected = districtIsSelected && !recipientIsSelected;

    let disabledCopy;

    if (anotherDistrictIsSelected) {
      disabledCopy = DisabledComposerListItemCopy.DISTRICT_IS_SELECTED;
    } else if (aboveSendToFewLimit && !recipientIsSelected) {
      disabledCopy = DisabledComposerListItemCopy.SEND_TO_FEW_LIMIT_REACHED(sendToFewLimit);
    }

    if (organizationRecipients) {
      return (
        <OrganizationRecipientItem
          recipient={organizationRecipients[0]}
          recipientIsSelected={recipientIsSelected}
          disabledCopy={
            nonDistrictRecipientIsSelected
              ? DisabledComposerListItemCopy.NONDISTRICT_RECIPIENT_IS_SELECTED
              : disabledCopy
          }
        />
      );
    } else if (groupRecipients) {
      return (
        <GroupRecipientItem
          recipient={groupRecipients[0]}
          recipientIsSelected={recipientIsSelected}
          disabledCopy={disabledCopy}
        />
      );
    } else if (groupChatRecipients) {
      return (
        <GroupChatRecipientItem
          recipient={groupChatRecipients[0]}
          recipientIsSelected={recipientIsSelected}
          disabledCopy={disabledCopy}
        />
      );
    } else if (individualRecipients) {
      const { member } = individualRecipients[0];
      const memberName = member.getName();
      const chatIneligibleText = (state: string) => {
        switch (state) {
          case 'under_13':
            return ChatIneligibleCopy.UNDER_13(memberName);
          case 'pending':
            return ChatIneligibleCopy.PENDING(memberName);
          case 'unknown':
            return ChatIneligibleCopy.UNKNOWN(memberName);
          default:
            return ChatIneligibleCopy.UNAVAILABLE(memberName);
        }
      };
      return (
        <IndividualRecipientItem
          recipient={individualRecipients[0]}
          recipientIsSelected={recipientIsSelected}
          disabledCopy={
            member.isEligible() ? (
              disabledCopy
            ) : (
              <React.Fragment>
                {chatIneligibleText(member.getState()).h1}
                <Text size="xxxsmall" color="wolf" element="div">
                  {chatIneligibleText(member.getState()).h2}
                </Text>
              </React.Fragment>
            )
          }
        />
      );
    }
    return <ErrorReporter error="empty content in RecipientItem" />;
  }
}
